import { useEffect, useMemo, useState } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import {
  buildPropertyExplorerRows,
  filterPropertyExplorerRows,
  type PeriodMode,
  type PropertyExplorerStatements,
} from '../../functions/calculations';
import type { PropertyExplorerRow } from '../../functions/calculations';
import type { ClientOverview, Unit } from '../../types/api';
import { usePortfolioOverview } from './usePortfolioOverview';

interface UsePropertyExplorerInput {
  overviewData: ClientOverview[];
  allUnits: Unit[];
  periodMode: PeriodMode;
}

export interface PropertyExplorerState {
  rows: PropertyExplorerRow[];
  visibleRows: PropertyExplorerRow[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  currencyCode: string;
  /** Mid-sentence description of the selected calculation window. */
  periodCaption: string;
  loading: boolean;
  error: string | null;
}

/**
 * Loads per-unit statements once, then rebuilds the table when the period
 * or display currency changes.
 */
export function usePropertyExplorer({
  overviewData,
  allUnits,
  periodMode,
}: UsePropertyExplorerInput): PropertyExplorerState {
  const { selectedClient, clientUnits, selectedCurrency, currencies } = useFilter();
  const [statements, setStatements] = useState<PropertyExplorerStatements | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overview = usePortfolioOverview();

  const currencyCode = selectedCurrency?.currency || 'USD';

  useEffect(() => {
    if (!selectedClient || !clientUnits.length || !overviewData.length) {
      setStatements(null);
      setLoading(false);
      setError(null);
      return;
    }
    if (overview.loading) {
      setLoading(true);
      return;
    }
    if (!overview.data) {
      setStatements(null);
      setLoading(false);
      setError(overview.error || 'Property details could not be loaded. Please try again.');
      return;
    }
    setStatements({
      balanceSheetRows: overview.data.balance_sheet,
      incomeRows: overview.data.income_statement,
      cashflowRows: overview.data.cashflow,
    });
    setLoading(false);
    setError(null);
  }, [selectedClient, clientUnits, overviewData, overview.data, overview.loading, overview.error]);

  const built = useMemo(() => {
    if (
      !statements ||
      !selectedClient ||
      !overviewData.length ||
      overviewData.some((row) => row.user_id_no !== selectedClient.user_id_no)
    ) {
      return { rows: [], periodCaption: '' };
    }

    return buildPropertyExplorerRows({
      userId: selectedClient.user_id_no,
      clientUnits,
      overviewData,
      allUnits,
      targetCurrency: currencyCode,
      currencies,
      statements,
      periodMode,
    });
  }, [
    statements,
    selectedClient,
    clientUnits,
    overviewData,
    allUnits,
    currencyCode,
    currencies,
    periodMode,
  ]);

  const visibleRows = useMemo(
    () => filterPropertyExplorerRows(built.rows, searchTerm),
    [built.rows, searchTerm],
  );

  return {
    rows: built.rows,
    visibleRows,
    searchTerm,
    setSearchTerm,
    currencyCode,
    periodCaption: built.periodCaption,
    loading,
    error,
  };
}
